import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import axios from 'axios';
import { Auth } from 'aws-amplify';
import API, { GraphQLResult, GRAPHQL_AUTH_MODE } from '@aws-amplify/api';
import { getPropertyInfo } from './graphql/queries';
import { makeStyles } from '@material-ui/core/styles';

import Login from './componenets/Login';
import Home from './componenets/Home';
import CheckIn from './componenets/CheckIn';
import CheckOut from './componenets/CheckOut';
import ExtraBed from './componenets/ExtraBed';
import AdvanceAmount from './componenets/AdvanceAmount';
import GuestRegister from './componenets/GuestRegister';
import Laundry from './componenets/Laundry';
import Invoice from './componenets/Invoice';
import Reports from './componenets/Reports';
import FoodBills from './componenets/FoodBills';
import RoomTransfer from './componenets/RoomTransfer';
import GstRecord from './componenets/GstRecord';
import RoomCleaners from './componenets/RoomCleaners';
import EditPackage from './componenets/EditPackage';
import UpgradeOccupancy from './componenets/UpgradeOccupancy';
import CheckinNotes from './componenets/CheckinNotes';
import RoomAvailabilityDetails from './componenets/RoomAvailabilityDetails';
import PositionListDetails from './componenets/PositionListDetails';
import AdvanceListDetails from './componenets/AdvanceListDetails';
import CreditListDetails from './componenets/CreditListDetails';
import AdvanceBookings from './componenets/AdvanceBookings';
import UpdateCheckIn from './componenets/UpdateCheckIn';
import NewCheckinRegister from './componenets/NewCheckinRegister';
import RestaurantInvoice from './componenets/PosInvoice';
import NotFound from './componenets/NotFound';
import { Loading } from './stories/Loading';

import './App.css';

import { withAuthenticator } from 'aws-amplify-react';

import { Amplify } from 'aws-amplify';
import awsconfig from './aws-exports';
Amplify.configure(awsconfig);

const { NODE_ENV } = process.env;
const isProduction = NODE_ENV === 'production';

const useStyles = makeStyles((theme) => ({
  loadingContainer: {
    textAlign: 'start',
    height: 'calc(100VH - 120px)',
    justifyContent: 'center',
    alignItems: 'center',
    display: 'flex',
    width: '100VW',
  },
}));

export const FeatureContext = React.createContext({
  packageFeatureEnabled: false,
  foodTypeFeatureEnabled: false,
  groupRoomsByBlockFeatureEnabled: false,
  singleOccupancyFeatureEnabled: false,
  refTariffGstInclFeatureEnabled: false,
  confirmPayModeFeatureEnabled: false,
  posInvoiceFeatureEnabled: false,
});

function App() {
  const [propertyInfo, setPropertyInfo] = useState<any>();
  const [isLoading, setLoading] = useState(true);
  const [tenantId, setTenantId] = useState<string>();
  const [departmentName, setDepartmentName] = useState<string>();
  const classes = useStyles();

  useEffect(() => {
    if (isProduction) {
      (async () => {
        let userInfo = await Auth.currentUserInfo();
        console.log(userInfo);
        setTenantId(userInfo.attributes['custom:client_id']);
        setDepartmentName(userInfo.attributes['custom:department_name']);
        console.log(userInfo.attributes['custom:department_name']);
      })();
    } else {
      setDepartmentName('Reception');
      setPropertyInfo({
        brandName: 'Demo Hotel',
      });
    }
  }, []);

  useEffect(() => {
    if (isProduction) {
      if (tenantId) {
        fetchPropertyInfo(tenantId);

        // Pass User ID token to resolve tenantId in server.
        axios.interceptors.request.use(
          async (config) => {
            try {
              const token = `Bearer ${(await Auth.currentSession())
                .getIdToken()
                .getJwtToken()}`;
              config.headers['API_AUTH'] = token;
              config.headers['x-auth-token'] = token;
              return config;
            } catch (err) {}
            return config;
          },
          (error) => {
            return Promise.reject(error);
          }
        );
      }
      // console.log('InterceptorId: ', interceptorId);
    } else {
      setPropertyInfo({
        brandName: 'Demo Hotel',
        gstNumber: '',
        invoiceTemplate: 'invoice.svg',
        packageFeatureEnabled: false,
        foodTypeFeatureEnabled: false,
        groupRoomsByBlockFeatureEnabled: false,
        confirmPayModeFeatureEnabled: true,
        posInvoiceFeatureEnabled: false,
      });
      setLoading(false);
    }
  }, [tenantId]);

  const fetchPropertyInfo = async (tenantId: string) => {
    try {
      const { data } = (await API.graphql({
        query: getPropertyInfo,
        variables: { id: tenantId, limit: 1 },
        authMode: GRAPHQL_AUTH_MODE.API_KEY,
      })) as GraphQLResult<any>;

      setPropertyInfo(data.getPropertyInfo);
    } catch (err) {
      console.log('Err:', err);
      setPropertyInfo(undefined);
    } finally {
      setLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className={classes.loadingContainer}>
        <Loading />
      </div>
    );
  } else if (propertyInfo) {
    switch (departmentName) {
      case 'Accounts':
        return (
          <div className="App">
            <header className="App-header">
              <h1 className="App-title">{propertyInfo.brandName}</h1>
            </header>
            <Router>
              <FeatureContext.Provider
                value={{
                  packageFeatureEnabled: propertyInfo.packageFeatureEnabled,
                  foodTypeFeatureEnabled: propertyInfo.foodTypeFeatureEnabled,
                  groupRoomsByBlockFeatureEnabled:
                    propertyInfo.groupRoomsByBlockFeatureEnabled,
                  singleOccupancyFeatureEnabled:
                    propertyInfo.singleOccupancyFeatureEnabled,
                  refTariffGstInclFeatureEnabled:
                    propertyInfo.refTariffGstInclFeatureEnabled,
                  confirmPayModeFeatureEnabled:
                    propertyInfo.confirmPayModeFeatureEnabled,
                  posInvoiceFeatureEnabled:
                    propertyInfo.posInvoiceFeatureEnabled,
                }}
              >
                <Routes>
                  <Route
                    path="/"
                    element={
                      <Home
                        isProduction={isProduction}
                        packageFeatureEnabled={
                          propertyInfo.packageFeatureEnabled
                        }
                        upgradeOccupancyFeatureEnabled={
                          propertyInfo.singleOccupancyFeatureEnabled
                        }
                        departmentName={departmentName || ''}
                        posInvoiceFeatureEnabled={
                          propertyInfo.posInvoiceFeatureEnabled
                        }
                      />
                    }
                  />

                  {propertyInfo.posInvoiceFeatureEnabled ? (
                    <Route
                      path="/posInvoice"
                      element={<RestaurantInvoice />}
                    />
                  ) : (
                    []
                  )}

                  <Route
                    path="/advanceAmount"
                    element={<AdvanceAmount accountsView />}
                  />
                  <Route path="/invoice" element={<Invoice />} />
                  <Route path="/reports" element={<Reports />} />
                  <Route path="/foodBills" element={<FoodBills accountsView />} />
                  <Route
                    path="/laundry"
                    element={<Laundry accountsView />}
                  />
                  <Route
                    path="/roomAvailability"
                    element={<RoomAvailabilityDetails />}
                  />
                  <Route
                    path="/positionList"
                    element={<PositionListDetails />}
                  />
                  <Route
                    path="/advanceList"
                    element={<AdvanceListDetails />}
                  />
                  <Route path="/creditList" element={<CreditListDetails />} />
                  <Route path="/404" element={<NotFound />} />
                </Routes>
              </FeatureContext.Provider>
            </Router>
          </div>
        );
      case 'Reception':
        return (
          <div className="App">
            <header className="App-header">
              <h1 className="App-title">{propertyInfo.brandName}</h1>
            </header>
            <Router>
              <FeatureContext.Provider
                value={{
                  packageFeatureEnabled: propertyInfo.packageFeatureEnabled,
                  foodTypeFeatureEnabled: propertyInfo.foodTypeFeatureEnabled,
                  groupRoomsByBlockFeatureEnabled:
                    propertyInfo.groupRoomsByBlockFeatureEnabled,
                  singleOccupancyFeatureEnabled:
                    propertyInfo.singleOccupancyFeatureEnabled,
                  refTariffGstInclFeatureEnabled:
                    propertyInfo.refTariffGstInclFeatureEnabled,
                  confirmPayModeFeatureEnabled:
                    propertyInfo.confirmPayModeFeatureEnabled,
                  posInvoiceFeatureEnabled:
                    propertyInfo.posInvoiceFeatureEnabled,
                }}
              >
                <Routes>
                  <Route
                    path="/"
                    element={
                      <Home
                        isProduction={isProduction}
                        packageFeatureEnabled={
                          propertyInfo.packageFeatureEnabled
                        }
                        upgradeOccupancyFeatureEnabled={
                          propertyInfo.singleOccupancyFeatureEnabled
                        }
                        departmentName={departmentName || ''}
                        posInvoiceFeatureEnabled={
                          propertyInfo.posInvoiceFeatureEnabled
                        }
                      />
                    }
                  />
                  <Route path="/checkIn" element={<CheckIn />} />
                  <Route path="/checkOut" element={<CheckOut />} />
                  <Route path="/extraBed" element={<ExtraBed />} />
                  <Route path="/advanceAmount" element={<AdvanceAmount />} />
                  <Route path="/guestRegister" element={<GuestRegister />} />
                  <Route path="/laundry" element={<Laundry />} />
                  <Route path="/invoice" element={<Invoice />} />
                  <Route path="/reports" element={<Reports />} />
                  <Route path="/foodBills" element={<FoodBills />} />
                  <Route path="/gstRecord" element={<GstRecord />} />
                  <Route path="/roomTransfer" element={<RoomTransfer />} />
                  <Route path="/roomCleaners" element={<RoomCleaners />} />
                  <Route
                    path="/advancebookings"
                    element={<AdvanceBookings />}
                  />
                  <Route path="/updateCheckin" element={<UpdateCheckIn />} />

                  {propertyInfo.posInvoiceFeatureEnabled ? (
                    <Route
                      path="/posInvoice"
                      element={<RestaurantInvoice />}
                    />
                  ) : (
                    []
                  )}

                  <Route
                    path="/newCheckinRegister"
                    element={<NewCheckinRegister />}
                  />
                  {propertyInfo.packageFeatureEnabled ? (
                    <Route path="/editPackage" element={<EditPackage />} />
                  ) : (
                    []
                  )}

                  {propertyInfo.singleOccupancyFeatureEnabled ? (
                    <Route
                      path="/upgradeOccupancy"
                      element={<UpgradeOccupancy />}
                    />
                  ) : (
                    []
                  )}

                  <Route path="/checkinNotes" element={<CheckinNotes />} />
                  <Route
                    path="/roomAvailability"
                    element={<RoomAvailabilityDetails />}
                  />
                  <Route
                    path="/positionList"
                    element={<PositionListDetails />}
                  />
                  <Route
                    path="/advanceList"
                    element={<AdvanceListDetails />}
                  />
                  <Route path="/creditList" element={<CreditListDetails />} />
                  <Route path="/404" element={<NotFound />} />
                </Routes>
              </FeatureContext.Provider>
            </Router>
          </div>
        );
      case 'House Keeping':
        return (
          <div className="App">
            <header className="App-header">
              <h1 className="App-title">{propertyInfo.brandName}</h1>
            </header>
            <Router>
              <Routes>
                <Route
                  path="/"
                  element={
                    <Home
                      isProduction={isProduction}
                      departmentName={departmentName || ''}
                      packageFeatureEnabled={false}
                      upgradeOccupancyFeatureEnabled={false}
                    />
                  }
                />
                <Route path="/extraBed" element={<ExtraBed />} />
                <Route path="/laundry" element={<Laundry />} />
                <Route path="/roomCleaners" element={<RoomCleaners />} />
                <Route
                  path="/roomAvailability"
                  element={<RoomAvailabilityDetails />}
                />
                <Route
                  path="/positionList"
                  element={<PositionListDetails />}
                />
                <Route path="/404" element={<NotFound />} />
              </Routes>
            </Router>
          </div>
        );
      default:
        return (
          <div className="App">
            <header className="App-header">
              <h1 className="App-title">{propertyInfo.brandName}</h1>
            </header>
            <Router>
              <Routes>
                <Route
                  path="/"
                  element={
                    <Home
                      isProduction={isProduction}
                      departmentName={departmentName || ''}
                      packageFeatureEnabled={propertyInfo.packageFeatureEnabled}
                      upgradeOccupancyFeatureEnabled={false}
                    />
                  }
                />
                <Route path="/404" element={<NotFound />} />
              </Routes>
            </Router>
          </div>
        );
    }
  } else {
    return (
      <div className="App">
        <header className="App-header">
          <h1 className="App-title">User Not Configured Correctly!</h1>
        </header>
      </div>
    );
  }
}

export default isProduction ? withAuthenticator(App, false, [<Login />]) : App;
